// File: src/components/admin/scheduler/SchedulerHealthPanel.js
// Health check & repair panel for scheduler DOs

import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    LinearProgress,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Tooltip,
    Alert,
} from '@mui/material';
import {
    HealthAndSafety as HealthIcon,
    Build as RepairIcon,
    Visibility as ViewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as HealthyIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../../services/adminApi';

const SchedulerHealthPanel = ({ onViewUser }) => {
    const [loading, setLoading] = useState(false);
    const [repairing, setRepairing] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(true);

    const runHealthCheck = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await adminApi.bulkHealthCheck({});
            setResult(data);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const runRepairAll = async () => {
        setRepairing(true);
        setError(null);
        try {
            const data = await adminApi.bulkRepairSchedulers();
            setResult(data);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(message);
        } finally {
            setRepairing(false);
        }
    };

    const unhealthyResults = result?.results?.filter(r =>
        ['missing_alarm', 'overdue', 'drifted', 'error'].includes(r.status)
    ) || [];

    const getStatusColor = (status) => {
        switch (status) {
            case 'missing_alarm': return 'warning';
            case 'overdue': return 'error';
            case 'drifted': return 'warning';
            case 'error': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'missing_alarm':
            case 'drifted':
                return <WarningIcon fontSize="small" />;
            case 'overdue':
            case 'error':
                return <ErrorIcon fontSize="small" />;
            default:
                return <HealthyIcon fontSize="small" />;
        }
    };

    return (
        <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <HealthIcon color="primary" />
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                    Scheduler Health
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<HealthIcon />}
                    onClick={runHealthCheck}
                    disabled={loading || repairing}
                >
                    {loading ? 'Checking...' : 'Run Health Check'}
                </Button>
                {unhealthyResults.length > 0 && (
                    <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        startIcon={<RepairIcon />}
                        onClick={runRepairAll}
                        disabled={loading || repairing}
                    >
                        {repairing ? 'Repairing...' : 'Repair All'}
                    </Button>
                )}
            </Box>

            {(loading || repairing) && <LinearProgress />}

            {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                    {error}
                </Alert>
            )}

            {result && (
                <Box sx={{ p: 2 }}>
                    {/* Summary Chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip
                            icon={<HealthyIcon />}
                            label={`${result.summary.healthy} healthy`}
                            color="success"
                            variant={result.summary.healthy > 0 ? 'filled' : 'outlined'}
                            size="small"
                        />
                        {result.summary.unhealthy > 0 && (
                            <Chip
                                icon={<WarningIcon />}
                                label={`${result.summary.unhealthy} unhealthy`}
                                color="warning"
                                size="small"
                            />
                        )}
                        {result.summary.repaired > 0 && (
                            <Chip
                                icon={<RepairIcon />}
                                label={`${result.summary.repaired} repaired`}
                                color="info"
                                size="small"
                            />
                        )}
                        {result.summary.errors > 0 && (
                            <Chip
                                icon={<ErrorIcon />}
                                label={`${result.summary.errors} errors`}
                                color="error"
                                size="small"
                            />
                        )}
                        <Chip
                            label={`${result.summary.idle} idle`}
                            variant="outlined"
                            size="small"
                        />
                        <Chip
                            label={`${result.summary.total} total`}
                            variant="outlined"
                            size="small"
                        />
                    </Box>

                    {/* Unhealthy Results */}
                    {unhealthyResults.length > 0 && (
                        <>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                                onClick={() => setExpanded(!expanded)}
                            >
                                <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                    Unhealthy Schedulers ({unhealthyResults.length})
                                </Typography>
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Box>
                            <Collapse in={expanded}>
                                <List dense disablePadding>
                                    {unhealthyResults.map((r, idx) => (
                                        <ListItem
                                            key={r.uid || idx}
                                            sx={{
                                                borderLeft: 3,
                                                borderColor: `${getStatusColor(r.status)}.main`,
                                                mb: 0.5,
                                                bgcolor: 'action.hover',
                                                borderRadius: 1,
                                            }}
                                        >
                                            {getStatusIcon(r.status)}
                                            <ListItemText
                                                sx={{ ml: 1 }}
                                                primary={r.uid}
                                                secondary={
                                                    <>
                                                        <Chip
                                                            label={r.status}
                                                            size="small"
                                                            color={getStatusColor(r.status)}
                                                            sx={{ mr: 1 }}
                                                        />
                                                        {r.issue}
                                                        {r.repaired && (
                                                            <Chip label="repaired" size="small" color="info" sx={{ ml: 1 }} />
                                                        )}
                                                    </>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <Tooltip title="View User">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onViewUser?.(r.uid)}
                                                    >
                                                        <ViewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </>
                    )}

                    {unhealthyResults.length === 0 && result.summary.total > 0 && (
                        <Alert severity="success" variant="outlined">
                            All scheduler DOs are healthy.
                        </Alert>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default SchedulerHealthPanel;
